/* ============================================================
   SH4PE storefront behaviours — lightweight, dependency-free.
   All handlers are delegated and scoped to SH4PE nodes so they
   never interfere with the host theme's own scripts.
   ============================================================ */
(function () {
  'use strict';

  var money = function (cents, format) {
    // Fallback formatter. Shopify money_format is passed in from Liquid.
    if (window.Shopify && window.Shopify.formatMoney) {
      try { return window.Shopify.formatMoney(cents, format); } catch (e) {}
    }
    var v = (cents / 100).toFixed(2);
    return (format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, v);
  };

  /* ---------- FAQ / generic accordion ---------- */
  function initAccordions(root) {
    root.querySelectorAll('.sh4pe-faq__q').forEach(function (btn) {
      if (btn.dataset.sh4peBound) return;
      btn.dataset.sh4peBound = '1';
      btn.addEventListener('click', function () {
        var panel = btn.parentElement.querySelector('.sh4pe-faq__a');
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) panel.style.maxHeight = open ? '0px' : panel.scrollHeight + 'px';
      });
    });
  }

  /* ---------- Size-guide / generic modal ---------- */
  function initModals(root) {
    root.querySelectorAll('[data-sh4pe-modal-open]').forEach(function (opener) {
      if (opener.dataset.sh4peBound) return;
      opener.dataset.sh4peBound = '1';
      opener.addEventListener('click', function () {
        var modal = document.getElementById(opener.getAttribute('data-sh4pe-modal-open'));
        if (!modal) return;
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        var focusable = modal.querySelector('.sh4pe-modal__close');
        if (focusable) focusable.focus();
      });
    });
    root.querySelectorAll('.sh4pe-modal').forEach(function (modal) {
      if (modal.dataset.sh4peBound) return;
      modal.dataset.sh4peBound = '1';
      function close() { modal.hidden = true; document.body.style.overflow = ''; }
      modal.querySelectorAll('[data-sh4pe-modal-close]').forEach(function (c) {
        c.addEventListener('click', close);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) close();
      });
    });
  }

  /* ---------- Quantity steppers ---------- */
  function initQty(root) {
    root.querySelectorAll('.sh4pe-qty').forEach(function (wrap) {
      if (wrap.dataset.sh4peBound) return;
      wrap.dataset.sh4peBound = '1';
      var input = wrap.querySelector('input');
      wrap.querySelectorAll('button').forEach(function (b) {
        b.addEventListener('click', function () {
          var step = b.getAttribute('data-step') === 'down' ? -1 : 1;
          var v = Math.max(1, (parseInt(input.value, 10) || 1) + step);
          input.value = v;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ---------- Product gallery (responsive swipeable slider) ---------- */
  function initGalleries(root) {
    root.querySelectorAll('[data-sh4pe-gallery]').forEach(function (g) {
      if (g.dataset.sh4peBound) return;
      g.dataset.sh4peBound = '1';
      var track = g.querySelector('[data-sh4pe-gal-track]');
      if (!track) return;
      var slides = Array.prototype.slice.call(track.querySelectorAll('[data-sh4pe-gal-slide]'));
      var dots = Array.prototype.slice.call(g.querySelectorAll('[data-sh4pe-gal-dot]'));
      var thumbs = Array.prototype.slice.call(g.querySelectorAll('[data-sh4pe-gal-thumb]'));
      var thumbsWrap = g.querySelector('[data-sh4pe-gal-thumbs]');
      if (slides.length < 2) return;

      function activeIndex() {
        var trackLeft = track.getBoundingClientRect().left;
        var best = 0, min = Infinity;
        slides.forEach(function (sl, idx) {
          var d = Math.abs(sl.getBoundingClientRect().left - trackLeft);
          if (d < min) { min = d; best = idx; }
        });
        return best;
      }
      function setActive(i) {
        dots.forEach(function (d, idx) { d.setAttribute('aria-current', idx === i ? 'true' : 'false'); });
        thumbs.forEach(function (t, idx) { t.setAttribute('aria-current', idx === i ? 'true' : 'false'); });
        var at = thumbs[i];
        if (at && thumbsWrap) {
          thumbsWrap.scrollTo({ left: at.offsetLeft - (thumbsWrap.clientWidth / 2) + (at.clientWidth / 2), behavior: 'smooth' });
        }
      }
      function goTo(i) {
        var sl = slides[i];
        if (!sl) return;
        var delta = sl.getBoundingClientRect().left - track.getBoundingClientRect().left;
        track.scrollTo({ left: track.scrollLeft + delta, behavior: 'smooth' });
      }
      dots.forEach(function (d, idx) { d.addEventListener('click', function () { goTo(idx); }); });
      thumbs.forEach(function (t, idx) { t.addEventListener('click', function () { goTo(idx); }); });
      var raf;
      track.addEventListener('scroll', function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () { setActive(activeIndex()); });
      }, { passive: true });
      window.addEventListener('resize', function () { setActive(activeIndex()); });
      setActive(0);
    });
  }

  /* ---------- Variant picker + price sync + form wiring ----------
     Operates at PAGE level (one product per PDP) so the hero purchase
     panel and the sticky mobile bar always share the same selection. */
  function initVariantPickers(root) {
    if (document.documentElement.dataset.sh4peVariantsBound) return;
    var dataEl = document.querySelector('[data-sh4pe-variants]');
    if (!dataEl) return;
    document.documentElement.dataset.sh4peVariantsBound = '1';

    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var moneyFormat = dataEl.getAttribute('data-money-format') || '${{amount}}';
    var optionCount = parseInt(dataEl.getAttribute('data-option-count'), 10) || 1;
    var selected = {};

    // Seed selection from currently-pressed option buttons anywhere on the page.
    document.querySelectorAll('[data-sh4pe-option]').forEach(function (btn) {
      if (btn.getAttribute('aria-pressed') === 'true') {
        selected[btn.getAttribute('data-sh4pe-option')] = btn.getAttribute('data-value');
      }
    });

    // If nothing was seeded (e.g. the variant picker is hidden so a bundle app
    // can drive selection), default to the first available variant so the
    // add-to-cart button isn't stuck showing "Sold out".
    if (Object.keys(selected).length === 0) {
      var initial = variants.filter(function (v) { return v.available; })[0] || variants[0];
      if (initial) {
        for (var oi = 1; oi <= optionCount; oi++) { selected['option' + oi] = initial['option' + oi]; }
      }
    }

    function currentVariant() {
      return variants.find(function (v) {
        for (var i = 1; i <= optionCount; i++) {
          if (String(selected['option' + i]) !== String(v['option' + i])) return false;
        }
        return true;
      });
    }

    function render() {
      var v = currentVariant();
      if (v) {
        document.querySelectorAll('[data-sh4pe-price]').forEach(function (el) { el.textContent = money(v.price, moneyFormat); });
        document.querySelectorAll('[data-sh4pe-compare]').forEach(function (el) {
          if (v.compare_at_price && v.compare_at_price > v.price) { el.textContent = money(v.compare_at_price, moneyFormat); el.hidden = false; }
          else { el.hidden = true; }
        });
        document.querySelectorAll('[data-sh4pe-variant-id]').forEach(function (inp) { inp.value = v.id; });
        document.querySelectorAll('[data-sh4pe-sticky-variant]').forEach(function (el) { el.textContent = v.title; });
      }
      var avail = v && v.available;
      document.querySelectorAll('[data-sh4pe-atc]').forEach(function (btn) {
        btn.disabled = !avail;
        var lbl = btn.querySelector('[data-sh4pe-atc-label]') || btn;
        lbl.textContent = avail ? (btn.getAttribute('data-label') || 'Add to cart') : (btn.getAttribute('data-soldout') || 'Sold out');
      });
    }

    document.querySelectorAll('[data-sh4pe-option]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var opt = btn.getAttribute('data-sh4pe-option');
        selected[opt] = btn.getAttribute('data-value');
        document.querySelectorAll('[data-sh4pe-option="' + opt + '"]').forEach(function (o) {
          o.setAttribute('aria-pressed', o === btn ? 'true' : 'false');
        });
        document.querySelectorAll('[data-sh4pe-optionout="' + opt + '"]').forEach(function (out) {
          out.textContent = btn.getAttribute('data-value');
        });
        render();
      });
    });

    render();
  }

  /* ---------- Bundle selector (sets quantity) ---------- */
  function initBundles(root) {
    root.querySelectorAll('[data-sh4pe-bundles]').forEach(function (group) {
      if (group.dataset.sh4peBound) return;
      group.dataset.sh4peBound = '1';
      var scope = group.closest('[data-sh4pe-product]') || root;
      group.querySelectorAll('[data-sh4pe-bundle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          group.querySelectorAll('[data-sh4pe-bundle]').forEach(function (o) {
            o.setAttribute('aria-pressed', o === btn ? 'true' : 'false');
          });
          var qty = parseInt(btn.getAttribute('data-qty'), 10) || 1;
          scope.querySelectorAll('input[name="quantity"]').forEach(function (inp) {
            inp.value = qty; inp.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      });
    });
  }

  /* ---------- Reviews slider ---------- */
  function initReviewsSlider(root) {
    root.querySelectorAll('[data-sh4pe-reviews]').forEach(function (wrap) {
      if (wrap.dataset.sh4peBound) return;
      wrap.dataset.sh4peBound = '1';
      var track = wrap.querySelector('[data-sh4pe-reviews-track]');
      var prev = wrap.querySelector('[data-sh4pe-reviews-prev]');
      var next = wrap.querySelector('[data-sh4pe-reviews-next]');
      if (!track || !prev || !next) return;
      function step() {
        var card = track.querySelector('.sh4pe-review');
        return card ? card.getBoundingClientRect().width + 14 : track.clientWidth * 0.8;
      }
      function update() {
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      }
      prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Sticky mobile ATC visibility ---------- */
  function initSticky(root) {
    var bar = root.querySelector('[data-sh4pe-sticky]');
    if (!bar || bar.dataset.sh4peBound) return;
    bar.dataset.sh4peBound = '1';
    var anchor = document.querySelector('[data-sh4pe-atc-anchor]');
    // Sticky CTA scrolls up to the hero purchase area instead of adding to cart.
    var cta = root.querySelector('[data-sh4pe-sticky-cta]');
    if (cta) {
      cta.addEventListener('click', function () {
        var target = document.querySelector('[data-sh4pe-atc-anchor]') || document.querySelector('[data-sh4pe-product]');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
    function update() {
      var show;
      if (anchor) {
        var r = anchor.getBoundingClientRect();
        show = r.bottom < 0; // main ATC scrolled out of view
      } else {
        show = window.scrollY > 500;
      }
      bar.classList.toggle('is-visible', show);
    }
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function initAll(root) {
    root = root || document;
    root.querySelectorAll('.sh4pe-scope').forEach(function (s) {
      initAccordions(s); initModals(s); initQty(s);
      initGalleries(s); initVariantPickers(s); initBundles(s); initReviewsSlider(s);
    });
    initSticky(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else { initAll(document); }

  // Re-init inside the Shopify theme editor when sections are re-rendered.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });
})();
